import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ShieldCheck,
  Package,
  GitBranch,
  GraduationCap,
  Play,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  BarChart3,
  ChevronRight,
  Atom,
  Zap,
  Network,
} from 'lucide-react';
import type { ProjectType } from '@nirvik/shared';
import { apiClient } from '@/lib/api-client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AgentRun {
  runId: string;
  agentType: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  progressMessage: string;
  createdAt: string;
  completedAt: string | null;
  duration: number | null;
  findingSummary?: {
    total: number;
    critical: number;
    high: number;
    medium: number;
    low: number;
    info: number;
  };
  errorMessage?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const AGENT_INFO: Record<
  string,
  {
    name: string;
    description: string;
    longDescription: string;
    icon: React.ElementType;
    color: string;
    features: string[];
  }
> = {
  'code-vuln': {
    name: 'Code Vulnerability Scanner',
    description: 'AI-powered source code analysis',
    longDescription: 'Clones your repository, batches source files, and uses Claude to perform deep security analysis. Identifies injection flaws, XSS, hardcoded secrets, authentication issues, cryptographic weaknesses, and more. Generates auto-fix patches for high-confidence findings.',
    icon: ShieldCheck,
    color: 'blue',
    features: ['15+ vulnerability categories (OWASP Top 10, CWE)', 'Cross-file analysis for architectural issues', 'Auto-fix patches for high-confidence findings', 'Plain-language explanations for every finding', 'Supports JS, TS, Python, Go, Java, Rust, C/C++, Ruby, PHP'],
  },
  dependency: {
    name: 'Dependency Scanner',
    description: 'Known vulnerability database check',
    longDescription: 'Parses your lockfiles (package-lock.json, yarn.lock, pnpm-lock.yaml, requirements.txt, Cargo.lock, go.sum, and more) and checks every dependency against the OSV.dev vulnerability database. Identifies CVEs with severity scores and upgrade paths.',
    icon: Package,
    color: 'emerald',
    features: ['Supports npm, yarn, pnpm, pip, Go, Rust, Ruby, PHP', 'Real-time OSV.dev vulnerability database', 'CVSS severity scoring', 'Upgrade path recommendations', 'Transitive dependency analysis'],
  },
  education: {
    name: 'Security Education',
    description: 'Plain-language security learning',
    longDescription: 'Takes your existing findings and generates educational content for each one. Explains vulnerabilities in plain language with analogies, realistic attack scenarios, step-by-step fix guides with code examples, and links to learning resources.',
    icon: GraduationCap,
    color: 'amber',
    features: ['Plain-language vulnerability explanations', 'Realistic attack scenario descriptions', 'Step-by-step fix guides with code examples', 'Curated learning resource links', 'Difficulty-rated content (beginner to advanced)'],
  },
  'secret-scanner': {
    name: 'Secret Scanner',
    description: 'Hardcoded credential detection',
    longDescription: 'Scans your entire codebase for hardcoded secrets including AWS keys, GitHub tokens, Slack tokens, Stripe keys, private keys, database connection strings, and high-entropy strings. Uses LLM verification to distinguish real secrets from placeholders.',
    icon: ShieldCheck,
    color: 'red',
    features: ['AWS, GitHub, Slack, Stripe, Google API key detection', 'Private key and certificate detection', 'High-entropy string analysis', 'LLM-powered false positive filtering', 'CWE-798 compliance mapping'],
  },
  'license-compliance': {
    name: 'License Compliance',
    description: 'Dependency license audit',
    longDescription: 'Analyzes all dependencies in your project for license compliance risks. Categorizes licenses as permissive, copyleft, or restrictive and flags potential legal issues for proprietary projects.',
    icon: ShieldCheck,
    color: 'amber',
    features: ['MIT, BSD, Apache, ISC permissive license detection', 'GPL, LGPL, AGPL copyleft flagging', 'SSPL, BSL restrictive license warnings', 'Unknown/missing license detection', 'Recursive dependency license analysis'],
  },
  'container-security': {
    name: 'Container Security',
    description: 'Docker security analysis',
    longDescription: 'Analyzes Dockerfiles and docker-compose configurations for security misconfigurations. Checks for running as root, :latest tags, exposed ports, missing healthchecks, secrets in build args, and privileged mode.',
    icon: ShieldCheck,
    color: 'cyan',
    features: ['Dockerfile best practice validation', 'Running as root detection', 'Image tag pinning checks', 'Secret exposure in build args/ENV', 'docker-compose privilege analysis'],
  },
  'iac-scanner': {
    name: 'Infrastructure as Code',
    description: 'IaC security scanning',
    longDescription: 'Scans Terraform, CloudFormation, CDK, and other IaC templates for security misconfigurations. Detects overly permissive IAM, public S3 buckets, unencrypted resources, and wide-open security groups.',
    icon: ShieldCheck,
    color: 'sky',
    features: ['Terraform, CloudFormation, CDK support', 'IAM policy analysis for over-permission', 'Public resource exposure detection', 'Encryption-at-rest validation', 'Security group ingress/egress review'],
  },
  'api-security': {
    name: 'API Security',
    description: 'Endpoint security analysis',
    longDescription: 'Analyzes API endpoints, route definitions, and controllers for security issues. Finds missing authentication, rate limiting gaps, input validation failures, IDOR vulnerabilities, and excessive data exposure.',
    icon: ShieldCheck,
    color: 'fuchsia',
    features: ['Missing authentication detection', 'Rate limiting gap analysis', 'Input validation review', 'IDOR vulnerability detection', 'OpenAPI/Swagger spec analysis'],
  },
  sast: {
    name: 'SAST Engine',
    description: 'Pattern-based static analysis',
    longDescription: 'Uses advanced regex patterns to detect known dangerous code patterns (eval, exec, innerHTML, SQL concatenation) then verifies with LLM analysis. Catches injection, XSS, weak crypto, and command execution vulnerabilities.',
    icon: ShieldCheck,
    color: 'rose',
    features: ['Pattern-first detection (fast)', 'LLM-powered verification (accurate)', 'Command injection patterns', 'XSS sink detection', 'SQL injection pattern matching'],
  },
  'crypto-audit': {
    name: 'Cryptography Audit',
    description: 'Crypto implementation review',
    longDescription: 'Deep analysis of cryptographic implementations. Checks for weak hash algorithms, insufficient key lengths, ECB mode, missing IVs, hardcoded crypto keys, weak password hashing, and JWT security issues.',
    icon: ShieldCheck,
    color: 'violet',
    features: ['Weak algorithm detection (MD5, SHA1)', 'Key length validation (RSA, AES)', 'JWT security analysis', 'Password hashing review', 'Random number generation audit'],
  },
  'access-control': {
    name: 'Access Control Analysis',
    description: 'Authorization security review',
    longDescription: 'Analyzes authorization patterns across your codebase. Finds missing auth checks on routes, horizontal/vertical privilege escalation paths, IDOR vulnerabilities, and insecure direct object references.',
    icon: ShieldCheck,
    color: 'emerald',
    features: ['Route authorization audit', 'Privilege escalation detection', 'IDOR vulnerability scanning', 'Role hierarchy analysis', 'Session management review'],
  },
  'data-privacy': {
    name: 'Data Privacy',
    description: 'PII/PHI exposure detection',
    longDescription: 'Scans for personally identifiable information in logs, error messages, and unencrypted storage. Detects email, SSN, credit card, and health data patterns. Assesses GDPR/CCPA compliance gaps.',
    icon: ShieldCheck,
    color: 'pink',
    features: ['PII pattern detection (email, SSN, CC)', 'Log exposure analysis', 'Unencrypted storage detection', 'GDPR/CCPA gap analysis', 'Data retention assessment'],
  },
  'supply-chain': {
    name: 'Supply Chain Security',
    description: 'Software supply chain risks',
    longDescription: 'Analyzes your dependency ecosystem for supply chain attacks. Checks for typosquatting, malicious install scripts, dependency confusion, unpinned versions, and CI pipeline injection risks.',
    icon: ShieldCheck,
    color: 'orange',
    features: ['Typosquatting risk detection', 'Malicious install script scanning', 'CI pipeline injection analysis', 'Dependency confusion checks', 'Lockfile integrity verification'],
  },
  'cloud-config': {
    name: 'Cloud Configuration',
    description: 'Cloud service security review',
    longDescription: 'Reviews AWS, GCP, and Azure SDK usage patterns and cloud configurations for overly permissive IAM roles, publicly exposed resources, unencrypted storage, and missing access logging.',
    icon: ShieldCheck,
    color: 'indigo',
    features: ['AWS, GCP, Azure SDK analysis', 'IAM role permission review', 'Public resource detection', 'Cloud metadata endpoint protection', 'Storage encryption validation'],
  },
  'threat-model': {
    name: 'AI Threat Model',
    description: 'STRIDE threat modeling',
    longDescription: 'Generates a comprehensive STRIDE threat model for your entire codebase. Analyzes architecture, entry points, data flows, and trust boundaries to identify spoofing, tampering, repudiation, information disclosure, DoS, and privilege escalation threats.',
    icon: ShieldCheck,
    color: 'amber',
    features: ['STRIDE methodology', 'Architecture-level analysis', 'Data flow mapping', 'Trust boundary identification', 'Risk scoring (likelihood x impact)'],
  },
  compliance: {
    name: 'Compliance Mapping',
    description: 'Framework compliance assessment',
    longDescription: 'Maps your codebase against SOC 2, PCI DSS, HIPAA, GDPR, and OWASP ASVS compliance frameworks. Identifies missing controls and generates gap analysis with remediation guidance.',
    icon: ShieldCheck,
    color: 'lime',
    features: ['SOC 2 Trust Service Criteria', 'PCI DSS control mapping', 'HIPAA security rule checks', 'OWASP ASVS verification', 'Gap analysis with remediation'],
  },
  'pentest-recon': {
    name: 'Attack Surface Recon',
    description: 'Penetration test reconnaissance',
    longDescription: 'Maps your application attack surface like a penetration tester. Identifies HTTP endpoints, auth mechanisms, file upload handlers, external integrations, admin endpoints, WebSocket surfaces, and GraphQL schemas.',
    icon: ShieldCheck,
    color: 'red',
    features: ['HTTP endpoint enumeration', 'Authentication mechanism mapping', 'File upload handler detection', 'Admin/debug endpoint discovery', 'External integration inventory'],
  },
  'endpoint-surface': {
    name: 'Endpoint Surface Assessment',
    description: 'Live endpoint probing and risk prioritization',
    longDescription: 'Probes live endpoints for TLS configuration, security headers, and open-port exposure — then uses LLM reasoning across the combined output to prioritize real risks.',
    icon: ShieldCheck,
    color: 'red',
    features: ['TLS/SSL configuration probing', 'Security header analysis', 'Open port detection', 'LLM-powered risk prioritization', 'Cross-signal threat synthesis'],
  },
  'webapp-pentest': {
    name: 'Web App Pentest',
    description: 'Dynamic web application security testing',
    longDescription: 'Dynamic webapp probing: forms, sensitive-path enumeration, reflection canary injection, then agentic synthesis of follow-up tests based on discovered surface.',
    icon: ShieldCheck,
    color: 'red',
    features: ['Form input probing', 'Sensitive path enumeration', 'Reflection/XSS canary testing', 'Agentic follow-up test generation', 'Dynamic attack surface mapping'],
  },
  'api-pentest': {
    name: 'API Pentest',
    description: 'OpenAPI and GraphQL-driven API probing',
    longDescription: 'OpenAPI / GraphQL-driven API probing — detects auth drift, BOLA/IDOR candidates, and generates chain-attack hypotheses based on observed API behavior.',
    icon: ShieldCheck,
    color: 'red',
    features: ['OpenAPI/Swagger spec analysis', 'GraphQL schema probing', 'BOLA/IDOR candidate detection', 'Auth drift detection', 'Chain-attack hypothesis generation'],
  },
  'auth-attack': {
    name: 'Auth Attack',
    description: 'Authentication and OAuth security testing',
    longDescription: 'Enumerates login and OAuth endpoints, audits cookie flags and session behavior, decodes and validates JWTs, and flags weak authentication configurations.',
    icon: ShieldCheck,
    color: 'red',
    features: ['Login/OAuth endpoint enumeration', 'Cookie flag auditing', 'JWT decoding and validation', 'Session security analysis', 'Weak auth configuration detection'],
  },
  'asset-recon': {
    name: 'Asset Recon',
    description: 'Subdomain and DNS security reconnaissance',
    longDescription: 'Subdomain enumeration, SPF/DMARC email security audit, and dangling-CNAME takeover detection across your domain infrastructure.',
    icon: ShieldCheck,
    color: 'red',
    features: ['Subdomain enumeration', 'SPF/DMARC audit', 'Dangling CNAME takeover detection', 'DNS security analysis', 'Domain infrastructure mapping'],
  },
  'breach-intel': {
    name: 'Breach Intel',
    description: 'Credential and code exposure detection',
    longDescription: 'Cross-references certificate-transparency logs and GitHub code search for exposed credentials, secrets, and sensitive data tied to your domain.',
    icon: ShieldCheck,
    color: 'red',
    features: ['Certificate transparency log analysis', 'GitHub code search for secrets', 'Exposed credential detection', 'Domain-linked exposure discovery', 'Historical breach correlation'],
  },
  'graphql-pentest': {
    name: 'GraphQL Pentest',
    description: 'GraphQL-specific security probing',
    longDescription: 'GraphQL-specific probing: introspection schema analysis, depth-bomb and alias amplification tests, batching attacks, then LLM-generated follow-up tests.',
    icon: ShieldCheck,
    color: 'red',
    features: ['Introspection schema analysis', 'Depth-bomb attack testing', 'Alias amplification detection', 'Batching attack analysis', 'LLM-powered follow-up test generation'],
  },
  'malware-detect': {
    name: 'Malicious Code Detection',
    description: 'Backdoor and malware scanning',
    longDescription: 'Scans for patterns indicating malicious code: obfuscated payloads, reverse shells, crypto miners, data exfiltration, base64-encoded executables, and suspicious npm install scripts.',
    icon: ShieldCheck,
    color: 'red',
    features: ['Obfuscated code detection', 'Reverse shell pattern matching', 'Crypto miner identification', 'Data exfiltration patterns', 'Malicious npm script scanning'],
  },
  'config-audit': {
    name: 'Configuration Audit',
    description: 'App config security review',
    longDescription: 'Audits application configuration files for security issues. Checks for debug modes in production, verbose error reporting, weak session configs, insecure cookies, permissive CORS, and missing security headers.',
    icon: ShieldCheck,
    color: 'slate',
    features: ['Debug mode detection', 'Security header validation (CSP, HSTS)', 'Session configuration review', 'Cookie security analysis', 'CORS policy assessment'],
  },
  'network-security': {
    name: 'Network Security',
    description: 'Network config analysis',
    longDescription: 'Analyzes network-related configurations and source code for missing TLS/SSL, disabled certificate verification, cleartext traffic, insecure cipher suites, and exposed internal services.',
    icon: ShieldCheck,
    color: 'teal',
    features: ['TLS/SSL configuration review', 'Certificate verification checks', 'Cleartext traffic detection', 'Cipher suite analysis', 'Internal service exposure'],
  },
  'incident-response': {
    name: 'Incident Response Readiness',
    description: 'IR preparedness assessment',
    longDescription: 'Assesses your codebase for incident response readiness. Checks structured logging, audit trails, alerting integrations, error tracking, health checks, graceful shutdown, and rate limiting implementation.',
    icon: ShieldCheck,
    color: 'orange',
    features: ['Security event logging assessment', 'Audit trail completeness', 'Alert integration detection', 'Error tracking review', 'Rate limiting verification'],
  },
  'security-score': {
    name: 'Security Scorecard',
    description: 'Comprehensive security scoring',
    longDescription: 'Performs a broad assessment across 9 security dimensions: authentication, authorization, input validation, cryptography, dependencies, configuration, logging, error handling, and data protection. Generates a 0-100 score for each.',
    icon: ShieldCheck,
    color: 'blue',
    features: ['9 security dimensions scored 0-100', 'Authentication strength analysis', 'Encryption quality assessment', 'Configuration security evaluation', 'Actionable improvement recommendations'],
  },
  'cicd-gate': {
    name: 'CI/CD Security Gate',
    description: 'Pipeline security analysis',
    longDescription: 'Analyzes CI/CD pipeline configurations for security risks. Checks GitHub Actions, GitLab CI, Jenkins, and others for secret exposure, script injection, insecure third-party actions, and missing security scanning steps.',
    icon: GitBranch,
    color: 'purple',
    features: ['GitHub Actions, GitLab CI, Jenkins support', 'Secret exposure detection in CI logs', 'Script injection vulnerability scanning', 'Third-party action security review', 'OIDC authentication recommendations'],
  },
  'quantum-readiness': {
    name: 'Quantum Readiness',
    description: 'Post-quantum cryptography posture',
    longDescription: 'Tier-classifies the project against post-quantum cryptography requirements. Inspects TLS hybrid KEM negotiation, certificate chains, JWT signing algorithms, and crypto-library versions. Maps every finding to NIST PQC + NSA CNSA 2.0 controls so you can see exactly where the project is "quantum-vulnerable" and what to swap before the migration deadline.',
    icon: Atom,
    color: 'violet',
    features: ['TLS hybrid KEM (X25519MLKEM768) detection', 'Certificate signature algorithm review (RSA, ECDSA → ML-DSA / SLH-DSA)', 'JWT alg-header audit (RS256 / ES256 vs PQ)', 'Crypto library version checks against NIST PQC reference impls', 'NIST PQC + NSA CNSA 2.0 control mapping'],
  },
  'exploit-chains': {
    name: 'Exploit Chain Analysis',
    description: 'Multi-step attack chain synthesis',
    longDescription: 'Synthesises individual findings from other agents into multi-step attack chains and prioritises by exploitability. Reasons across categories — e.g. an SSRF + an unauthenticated metadata endpoint + an over-permissive IAM role becomes one critical "cloud takeover" chain instead of three medium findings reviewers tune out.',
    icon: Zap,
    color: 'red',
    features: ['Cross-finding chain synthesis', 'Exploitability-ranked prioritisation', 'Single-incident framing for related findings', 'Attack-narrative remediation guidance', 'Runs after sibling agents complete (Smart Scan)'],
  },
  'smart-scan': {
    name: 'Smart Scan',
    description: 'Multi-agent orchestrator',
    longDescription: 'Inspects the project type and language, picks the most relevant agents, and runs them in parallel. After they finish, queues exploit-chains synthesis and education enrichment so you see correlated chains and plain-language explanations without manually wiring up each agent.',
    icon: Network,
    color: 'indigo',
    features: ['Auto-selects relevant agents from project type', 'Parallel execution to compress wall time', 'Queues exploit-chains for cross-finding synthesis', 'Enqueues education for plain-language explanations', 'Single button → end-to-end scan'],
  },
};

const COLOR_MAP: Record<string, { bg: string; text: string }> = {
  blue: { bg: 'bg-blue-500/10', text: 'text-blue-700 dark:text-blue-400' },
  emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-700 dark:text-emerald-400' },
  purple: { bg: 'bg-purple-500/10', text: 'text-purple-700 dark:text-purple-400' },
  amber: { bg: 'bg-amber-500/10', text: 'text-amber-700 dark:text-amber-400' },
  red: { bg: 'bg-red-500/10', text: 'text-red-700 dark:text-red-400' },
  rose: { bg: 'bg-rose-500/10', text: 'text-rose-700 dark:text-rose-400' },
  violet: { bg: 'bg-violet-500/10', text: 'text-violet-700 dark:text-violet-400' },
  cyan: { bg: 'bg-cyan-500/10', text: 'text-cyan-700 dark:text-cyan-400' },
  sky: { bg: 'bg-sky-500/10', text: 'text-sky-700 dark:text-sky-400' },
  indigo: { bg: 'bg-indigo-500/10', text: 'text-indigo-700 dark:text-indigo-400' },
  pink: { bg: 'bg-pink-500/10', text: 'text-pink-700 dark:text-pink-400' },
  orange: { bg: 'bg-orange-500/10', text: 'text-orange-700 dark:text-orange-400' },
  lime: { bg: 'bg-lime-500/10', text: 'text-lime-400' },
  teal: { bg: 'bg-teal-500/10', text: 'text-teal-700 dark:text-teal-400' },
  fuchsia: { bg: 'bg-fuchsia-500/10', text: 'text-fuchsia-700 dark:text-fuchsia-400' },
  slate: { bg: 'bg-slate-500/10', text: 'text-slate-700 dark:text-slate-400' },
};

const STATUS_STYLES: Record<
  string,
  { bg: string; text: string; icon: React.ElementType; animate?: boolean }
> = {
  queued: { bg: 'bg-gray-500/10', text: 'text-muted-foreground', icon: Clock },
  running: { bg: 'bg-blue-500/10', text: 'text-blue-700 dark:text-blue-400', icon: Loader2, animate: true },
  completed: { bg: 'bg-emerald-500/10', text: 'text-emerald-700 dark:text-emerald-400', icon: CheckCircle2 },
  failed: { bg: 'bg-red-500/10', text: 'text-red-700 dark:text-red-400', icon: AlertTriangle },
  cancelled: { bg: 'bg-gray-500/10', text: 'text-muted-foreground', icon: Clock },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '-';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AgentDetailPage() {
  const { orgId, portfolioId, projectId, agentType } = useParams<{ orgId: string; portfolioId: string; projectId: string; agentType: string }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const agent = AGENT_INFO[agentType || ''];
  const enabled = !!orgId && !!portfolioId && !!projectId;
  const basePath = `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}`;

  const { data: configRes } = useQuery({
    queryKey: ['agent-configs', orgId, portfolioId, projectId],
    queryFn: () => apiClient.get(`${basePath}/agents/config`),
    enabled,
  });

  // Project record is the source of truth for target configuration — legacy
  // agent-config is only a fallback for pre-Phase-A projects.
  const { data: projectRes } = useQuery({
    queryKey: ['project', orgId, portfolioId, projectId],
    queryFn: () => apiClient.getProject(orgId!, portfolioId!, projectId!),
    enabled,
  });
  const project = projectRes?.data as
    | {
        projectType?: ProjectType;
        repoConfig?: { gitUrl: string; branch: string };
        endpointConfig?: { baseUrl: string };
      }
    | undefined;
  const projectType: ProjectType = project?.projectType ?? 'repo';

  const { data: runsRes, isLoading } = useQuery({
    queryKey: ['agent-runs', orgId, portfolioId, projectId, agentType],
    queryFn: () =>
      apiClient.get(`${basePath}/agents/runs?agentType=${agentType}`),
    enabled: enabled && !!agentType,
    refetchInterval: 5000,
  });

  const config = (configRes?.data as { repoUrl?: string; branch?: string } | undefined) ?? {};
  const runs = ((runsRes?.data as { runs?: AgentRun[] })?.runs ?? []) as AgentRun[];

  const startRun = useMutation({
    mutationFn: (params: { agentType: string; config: Record<string, unknown> }) =>
      apiClient.post(`${basePath}/agents/run`, params),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['agent-runs', orgId, portfolioId, projectId, agentType] }),
  });

  if (!agent) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">Agent type not found</p>
        <Link to={`${basePath}/agents`} className="text-blue-700 dark:text-blue-400 hover:underline mt-4 inline-block">
          Back to Agents
        </Link>
      </div>
    );
  }

  const colors = COLOR_MAP[agent.color] || COLOR_MAP.blue;
  const Icon = agent.icon;

  // Resolved target — prefer structured project config, fall back to legacy.
  const repoUrl: string | undefined = project?.repoConfig?.gitUrl ?? config.repoUrl;
  const repoBranch: string | undefined = project?.repoConfig?.branch ?? config.branch;
  const endpointBaseUrl: string | undefined = project?.endpointConfig?.baseUrl;
  const hasRepoTarget = !!repoUrl;
  const hasEndpointTarget = !!endpointBaseUrl;
  const hasTarget =
    (projectType === 'repo' && hasRepoTarget) ||
    (projectType === 'endpoint' && hasEndpointTarget) ||
    (projectType === 'hybrid' && (hasRepoTarget || hasEndpointTarget));
  const missingLabel =
    projectType === 'endpoint'
      ? 'endpoint'
      : projectType === 'hybrid'
        ? 'repo or endpoint'
        : 'repository';

  return (
    <div className="space-y-6">
      {/* Agent header — stacks on phones (icon+title inline, description
       * below, full-width Run button) and goes side-by-side from sm: up.
       * Switched items-center → items-start inside the icon/title group so
       * the icon doesn't float mid-paragraph when the description wraps
       * to multiple lines on a narrow viewport. */}
      <Card className="bg-card border border-border rounded-2xl p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-3 sm:gap-4 min-w-0">
            <div className={cn('h-12 w-12 sm:h-14 sm:w-14 rounded-xl flex items-center justify-center shrink-0', colors.bg)}>
              <Icon className={cn('h-6 w-6 sm:h-7 sm:w-7', colors.text)} />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-foreground break-words">{agent.name}</h1>
              <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{agent.longDescription}</p>
            </div>
          </div>
          <Button
            disabled={!hasTarget || startRun.isPending}
            onClick={() =>
              startRun.mutate({
                agentType: agentType!,
                // Legacy repo agents read repoUrl/branch from config; endpoint
                // agents load endpointConfig directly from DynamoDB (Phase A+).
                config: { repoUrl, branch: repoBranch },
              })
            }
            className="w-full sm:w-auto shrink-0"
          >
            {startRun.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
            Run Now
          </Button>
        </div>

        {!hasTarget && (
          <div className="mt-4 p-3 bg-yellow-500/5 border border-yellow-500/20 rounded-lg">
            <p className="text-sm text-yellow-700 dark:text-yellow-400">
              No {missingLabel} configured for this project.{' '}
              <Link to={`${basePath}/settings`} className="underline hover:text-yellow-800 dark:hover:text-yellow-300">
                Configure in Settings
              </Link>
            </p>
          </div>
        )}
      </Card>

      {/* Features grid */}
      <div>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Capabilities</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {agent.features.map((feature, i) => (
            <div
              key={i}
              className="flex items-start gap-2 p-3 bg-card border border-border rounded-xl"
            >
              <CheckCircle2 className={cn('h-4 w-4 mt-0.5 flex-shrink-0', colors.text)} />
              <span className="text-sm text-foreground">{feature}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Run history */}
      <div>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          Run History
        </h2>

        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="p-4 animate-pulse bg-card border-border">
                <div className="h-5 bg-muted rounded w-40 mb-2" />
                <div className="h-3 bg-muted rounded w-24" />
              </Card>
            ))}
          </div>
        ) : runs.length === 0 ? (
          <Card className="bg-card border border-border rounded-xl p-8 text-center">
            <BarChart3 className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No runs yet. Click "Run Now" to start.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {runs.map((run) => {
              const st = STATUS_STYLES[run.status] ?? STATUS_STYLES.queued;
              const StatusIcon = st.icon;
              return (
                <Card
                  key={run.runId}
                  className="bg-card border border-border rounded-xl p-4 hover:border-border transition-colors cursor-pointer"
                  onClick={() => navigate(`${basePath}/agents/runs/${run.runId}`)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={cn('h-9 w-9 rounded-lg flex items-center justify-center', st.bg)}>
                        <StatusIcon className={cn('h-4 w-4', st.text, st.animate && 'animate-spin')} />
                      </div>
                      <div>
                        <div className="flex items-center gap-3">
                          <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full capitalize', st.bg, st.text)}>
                            {run.status}
                          </span>
                          <span className="text-xs text-muted-foreground">{formatRelativeTime(run.createdAt)}</span>
                          {run.duration !== null && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Clock className="h-3 w-3" /> {formatDuration(run.duration)}
                            </span>
                          )}
                        </div>

                        {/* Progress bar for running/queued */}
                        {(run.status === 'running' || run.status === 'queued') && (
                          <div className="mt-2 w-64">
                            <Progress value={run.progress} className="h-1.5" />
                            <p className="text-xs text-muted-foreground mt-1">{run.progressMessage || `${run.progress}%`}</p>
                          </div>
                        )}

                        {/* Finding summary for completed */}
                        {run.status === 'completed' && run.findingSummary && run.findingSummary.total > 0 && (
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-muted-foreground">{run.findingSummary.total} findings:</span>
                            {run.findingSummary.critical > 0 && (
                              <span className="text-xs bg-red-500/10 text-red-700 dark:text-red-400 px-1.5 py-0.5 rounded">
                                {run.findingSummary.critical} critical
                              </span>
                            )}
                            {run.findingSummary.high > 0 && (
                              <span className="text-xs bg-orange-500/10 text-orange-700 dark:text-orange-400 px-1.5 py-0.5 rounded">
                                {run.findingSummary.high} high
                              </span>
                            )}
                            {run.findingSummary.medium > 0 && (
                              <span className="text-xs bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 px-1.5 py-0.5 rounded">
                                {run.findingSummary.medium} medium
                              </span>
                            )}
                            {run.findingSummary.low > 0 && (
                              <span className="text-xs bg-blue-500/10 text-blue-700 dark:text-blue-400 px-1.5 py-0.5 rounded">
                                {run.findingSummary.low} low
                              </span>
                            )}
                          </div>
                        )}

                        {/* Error for failed */}
                        {run.status === 'failed' && run.errorMessage && (
                          <p className="text-xs text-red-700 dark:text-red-400 mt-1 truncate max-w-md">{run.errorMessage}</p>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
